/**
 * Email Extractor
 * 
 * Responsible for extracting email data from Office.js email items.
 * Follows Single Responsibility Principle.
 */

/* global Office */

import { Logger } from '../utils/logger.js';
import { DateUtils } from '../utils/date-utils.js';

/**
 * Generates unique identifiers for items
 */
class UniqueIdGenerator {
  /**
   * Clean Internet Message ID by removing angle brackets and domain
   * Example: <5a031376083646f5a19596637ae43a7d@proteam.de> -> 5a031376083646f5a19596637ae43a7d
   */
  static cleanInternetMessageId(internetMessageId) {
    console.log('cleanInternetMessageId called with:', internetMessageId);
    
    if (!internetMessageId) {
      console.log('cleanInternetMessageId: No ID provided, returning as-is');
      return internetMessageId;
    }

    // Remove angle brackets if present
    let cleaned = internetMessageId.replace(/^<|>$/g, '');
    console.log('After removing angle brackets:', cleaned);
    
    // Extract the part before @ if present
    const atIndex = cleaned.indexOf('@');
    console.log('@ symbol found at index:', atIndex);
    
    if (atIndex > 0) {
      cleaned = cleaned.substring(0, atIndex);
      console.log('After extracting before @:', cleaned);
    }
    
    console.log('cleanInternetMessageId returning:', cleaned);
    return cleaned;
  }

  /**
   * Create a composite identifier from item properties
   */
  static createCompositeId(item, type) {
    const components = [type];
    
    // Use subject (first 50 chars)
    if (item?.subject) {
      const subject = typeof item.subject === 'string' ? item.subject : (item.subject?.value || '');
      if (subject) {
        components.push(subject.substring(0, 50));
      }
    }
    
    // For emails: use sender, for appointments: use organizer
    if (type === 'email' && item.from?.emailAddress) {
      components.push(item.from.emailAddress);
    } else if (type === 'appointment' && item.organizer?.emailAddress) {
      components.push(item.organizer.emailAddress);
    }
    
    // Use creation/start date
    const dateField = type === 'email' ? item.dateTimeCreated : item.start;
    if (dateField) {
      const dateStr = dateField instanceof Date ? dateField.toISOString() : dateField;
      components.push(dateStr);
    }
    
    // For emails: use first recipient, for appointments: use first attendee
    if (type === 'email' && item.to && item.to.length > 0) {
      components.push(item.to[0].emailAddress);
    } else if (type === 'appointment' && item.requiredAttendees && item.requiredAttendees.length > 0) {
      const firstAttendee = item.requiredAttendees[0];
      const email = firstAttendee?.emailAddress || firstAttendee?.address || firstAttendee;
      if (email) components.push(email);
    }

    // Create hash-like identifier
    return btoa(components.join('|')).replace(/[+/=]/g, '').substring(0, 32);
  }
}

/**
 * Normalizes and validates email addresses
 */
class EmailNormalizer {
  static normalize(emails) {
    if (!Array.isArray(emails)) {
      emails = emails ? [emails] : [];
    }
    
    return emails
      .filter((email) => typeof email === 'string' && email.trim().length > 0)
      .map((email) => email.toLowerCase())
      .filter((email, index, arr) => arr.indexOf(email) === index); // Remove duplicates
  }
}

/**
 * Determines email type (sent or received)
 */
class EmailTypeDetector {
  static async detect(emailItem) {
    // Get logged-in user's email from Outlook using Office.js API
    const outlookUserEmail = await new Promise((resolve) => {
      Office.context.mailbox.userProfile.emailAddress ? 
        resolve(Office.context.mailbox.userProfile.emailAddress.toLowerCase()) :
        resolve('');
    });
    
    const senderEmail = (emailItem.from?.emailAddress || '').toLowerCase();
    
    // Determine if this is a sent or received email
    const emailType = senderEmail === outlookUserEmail ? 'sent' : 'received';
    
    Logger.debug('Email type determination:', {
      senderEmail,
      outlookUserEmail,
      emailType
    });

    return { emailType, outlookUserEmail };
  }
}

/**
 * Extracts body content from email
 */
class EmailBodyExtractor {
  static async extract(emailItem) {
    return new Promise((resolve, reject) => {
      emailItem.body.getAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(new Error('Failed to get email body'));
        }
      });
    });
  }
}

/**
 * Main Email Extractor Service
 * Follows Single Responsibility Principle and Open/Closed Principle
 */
export class EmailExtractor {
  /**
   * Extract complete email data from Office.js email item
   */
  static async extractEmailData(emailItem) {
    // Get email body content asynchronously
    const body = await EmailBodyExtractor.extract(emailItem);

    // Detect email type
    const { emailType } = await EmailTypeDetector.detect(emailItem);
    
    const senderEmail = (emailItem.from?.emailAddress || '').toLowerCase();

    // Get unique email identifier
    const uniqueEmailId = this.getUniqueEmailId(emailItem);

    return {
      type: emailType,
      subject: emailItem.subject || '',
      body: body || '',
      senderEmail: senderEmail,
      receiversEmails: emailItem.to?.map(r => r.emailAddress.toLowerCase()) || [],
      CC: emailItem.cc?.map(r => r.emailAddress.toLowerCase()) || [],
      createdAt: DateUtils.toISOString(emailItem.dateTimeCreated),
      startDate: DateUtils.toISOString(emailItem.dateTimeCreated),
      endDate: DateUtils.toISOString(emailItem.dateTimeCreated),
      sentAt: DateUtils.toISOString(emailItem.dateTimeSent),
      uniqueId: uniqueEmailId
    };
  }

  /**
   * Get a single unique identifier for an email
   * Priority order: Internet Message ID > Item ID > Composite ID
   */
  static getUniqueEmailId(emailItem) {
    // Priority order for email identification:
    // 1. Internet Message ID (RFC 2822 standard, globally unique)
    if (emailItem.internetMessageId) {
      return UniqueIdGenerator.cleanInternetMessageId(emailItem.internetMessageId);
    }

    // 2. Item ID (Outlook's unique identifier)
    if (emailItem.itemId) {
      return emailItem.itemId;
    }

    // 3. Fallback: Create composite identifier
    return UniqueIdGenerator.createCompositeId(emailItem, 'email');
  }

  /**
   * Normalize email addresses
   */
  static normalizeEmails(emails) {
    return EmailNormalizer.normalize(emails);
  }
}

